import React, { Fragment, useState } from 'react'
import { useTranslation } from 'lib/i18n'
import { isServer } from 'lib/utils'
import Image from 'next/image'

import { Button } from 'components/UI/Button/Button'
import { ModalCard } from 'components/UI/ModalCard/ModalCard'
import Typography from 'components/UI/Typography/Typography'
import useResponsive from 'hooks/useResponsive'

import PaymentLogos from '../PaymentLogos/PaymentLogos'
import { TrustSectionProps } from '../type'
import checkMark from './check-mark.svg'
import laughing from './laughing.svg'
import trust from './trust.svg'

import styles from './TrustSection.module.css'

const TrustSection = ({ talentName }: TrustSectionProps): JSX.Element => {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const [showModal, setShowModal] = useState(false)
  const shouldRenderMobile = isMobile && !isServer()

  const RenderContainer = shouldRenderMobile ? ModalCard : Fragment
  const modalCardProps = shouldRenderMobile
    ? {
        onClose: () => setShowModal(!showModal),
        noRender: !showModal,
      }
    : {}

  const talentNameWithFallback =
    talentName || t('TrustSection.theCelebrity', 'The celebrity')

  return (
    <section className={styles.profileTrustSection} data-cy="profilePageTrust">
      {shouldRenderMobile && (
        <button
          type="button"
          className={styles.trustSectionLink}
          onClick={() => setShowModal(!showModal)}
          data-cy="profilePageTrustMobileLink"
        >
          <Image src={trust} width={18} height={19} alt="trust" />
          <span className={styles.trustSectionLinkText}>
            {t('TrustSection.link', 'Secure payments & quick delivery')}
          </span>
        </button>
      )}

      <RenderContainer {...modalCardProps}>
        <div data-cy="profilePageTrustContent" className={styles.trustSection}>
          {shouldRenderMobile && (
            <Typography.H5 className={styles.trustSectionUsps}>
              {t('TrustSection.usps', 'Secure, quick and easy')}
            </Typography.H5>
          )}

          <div className={styles.trustSectionBox}>
            <div className={styles.trustSectionBoxHeading}>
              <Image src={checkMark} width={20} height={20} alt="check-mark" />
              <Typography.BodyBold
                className={styles.trustSectionBoxHeadingText}
              >
                {t('TrustSection.moneyBackGuarantee', 'Money-back guarantee!')}
              </Typography.BodyBold>
            </div>
            <div className={styles.trustSectionBoxParagraph}>
              <Typography.Meta>
                {t(
                  'TrustSection.moneyBackGuaranteeParagraph',
                  'If you paid by card we reserve the amount when you place your order but only charge you once you have received the video.',
                )}
              </Typography.Meta>
            </div>
            <div className={styles.trustSectionBoxParagraph}>
              <Typography.Meta>
                {t(
                  'TrustSection.moneyBackGuaranteeParagraph2',
                  'If you paid by another method we will always do a full refund if the celebrity doesn’t respond.',
                )}
              </Typography.Meta>
              <PaymentLogos className={styles.paymentLogos} />
            </div>
          </div>

          <div className={styles.trustSectionBox}>
            <div className={styles.trustSectionBoxHeading}>
              <Image src={laughing} width={20} height={20} alt="happy-lol" />
              <Typography.BodyBold
                className={styles.trustSectionBoxHeadingText}
              >
                {t('TrustSection.quickDelivery', 'Quick delivery')}
              </Typography.BodyBold>
            </div>
            <div className={styles.trustSectionBoxParagraph}>
              <Typography.Meta>
                {t('TrustSection.quickDeliveryParagraph', {
                  defaultValue:
                    '{{talentName}} has 7 days to respond. Although... it`s usually quicker!',
                  talentName: talentNameWithFallback,
                })}
              </Typography.Meta>
            </div>
          </div>

          {shouldRenderMobile && (
            <div className={styles.trustSectionClose}>
              <Button onClick={() => setShowModal(!showModal)}>
                {t('TrustSection.close', 'Got it')}
              </Button>
            </div>
          )}
        </div>
      </RenderContainer>
    </section>
  )
}

export default TrustSection
